import React, { useMemo, useRef, useState } from 'react';
import { Select } from 'chakra-react-select';
import { useQuery } from '@tanstack/react-query';
import { useUsers } from '../../../../hooks/useUsers';
import { IData } from '../../../../types/shared';

// Usuário selecionável (responsável/supervisor do processo de estatuto).
export interface IUsuarioOption {
  id: number;
  nome: string;
}

interface UsuarioSelectProps {
  value: IUsuarioOption[];
  // eslint-disable-next-line no-unused-vars
  onChange: (usuarios: IUsuarioOption[]) => void;
  placeholder?: string;
  isInvalid?: boolean;
}

interface SelectOption {
  value: number;
  label: string;
}

// Multi-select de usuários com busca por nome (debounce). Reusa o esqueleto do
// CartorioSelect (chakra-react-select + useQuery por menuOpen) — Task #496.
export const UsuarioSelect: React.FC<UsuarioSelectProps> = ({
  value,
  onChange,
  placeholder = 'Buscar usuário pelo nome…',
  isInvalid = false,
}) => {
  const { getUsers } = useUsers();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const onInputChange = (val: string) => {
    setSearch(val);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(val), 400);
  };

  const { data, isFetching } = useQuery<IData<unknown>>({
    queryKey: ['usuarios-select', { search: debounced, page: 1, size: 20 }],
    queryFn: getUsers,
    enabled: menuOpen,
  });

  const options: SelectOption[] = useMemo(() => {
    const items = (data?.data?.items ?? []) as { id?: number; nome: string }[];
    return items
      .filter(u => u.id !== undefined)
      .map(u => ({ value: u.id as number, label: u.nome }));
  }, [data]);

  const selectValue: SelectOption[] = value.map(u => ({ value: u.id, label: u.nome }));

  return (
    <Select<SelectOption, true>
      isMulti
      placeholder={placeholder}
      options={options}
      value={selectValue}
      inputValue={search}
      onInputChange={onInputChange}
      onMenuOpen={() => setMenuOpen(true)}
      onMenuClose={() => setMenuOpen(false)}
      onChange={opts => onChange(opts.map(o => ({ id: o.value, nome: o.label })))}
      isLoading={isFetching}
      isClearable
      filterOption={null}
      noOptionsMessage={() => (debounced ? 'Nenhum usuário encontrado' : 'Digite para buscar')}
      loadingMessage={() => 'Buscando…'}
      size="sm"
      // chakra-react-select expõe o estado inválido via styles do controle.
      chakraStyles={
        isInvalid ? { control: provided => ({ ...provided, borderColor: 'red.500' }) } : undefined
      }
    />
  );
};
